import { Link, useParams } from "react-router";
import { LEGAL_DOCS, LEGAL_LINKS, LEGAL_VERSION, isLegalSlug, type LegalDoc } from "../../legal/legalContent";
import { AdMeasurementChoice } from "../../ui/privacy/AdMeasurementChoice";
import { PageShell } from "../../ui/PageShell";

/** `/legal` (the list) and `/legal/:slug` (one policy). Plain HTML: readable without WebGL or an account. */
export function LegalPage() {
  const { slug } = useParams();
  if (slug === undefined) return <LegalIndexView />;
  if (!isLegalSlug(slug)) {
    return (
      <PageShell title="Policy not found" narrow>
        <p>There is no policy at this address.</p>
        <p>
          <Link to="/legal">All terms and policies</Link>
        </p>
      </PageShell>
    );
  }
  return <LegalDocView doc={LEGAL_DOCS[slug]} />;
}

export function LegalIndexView() {
  return (
    <PageShell title="Terms and policies" narrow>
      <p className="small muted">{LEGAL_VERSION}</p>
      <ul className="legal-index">
        {LEGAL_LINKS.map((l) => (
          <li key={l.slug}>
            <Link to={`/legal/${l.slug}`}>{l.label}</Link>
            <div className="small muted">{LEGAL_DOCS[l.slug].summary}</div>
          </li>
        ))}
      </ul>
    </PageShell>
  );
}

export function LegalDocView({ doc }: { doc: LegalDoc }) {
  return (
    <PageShell title={doc.title}>
      <article className="legal-doc" data-testid={`legal-${doc.slug}`}>
        <p className="small muted">{LEGAL_VERSION}</p>
        <p className="legal-summary">{doc.summary}</p>
        {doc.sections.map((s) => (
          <section key={s.heading}>
            <h2>{s.heading}</h2>
            {s.paragraphs.map((p) => (
              <p key={p}>{p}</p>
            ))}
            {s.bullets ? (
              <ul>
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
        {doc.slug === "privacy" ? <AdMeasurementChoice /> : null}
        <p className="small">
          <Link to="/legal">All terms and policies</Link>
        </p>
      </article>
    </PageShell>
  );
}
